// ─────────────────────────────────────────────────────────────────────────────
// THE STAFF NAV, AND THE ONE ANSWER TO "MAY THIS ROLE SEE THIS PAGE".
//
// 🚨 THERE WERE THREE ANSWERS AND THEY DISAGREED. The sidebar filtered its
// items by role. The top navbar's NAV_LINKS filtered by nothing at all. The
// router filtered by nothing and said so — "Defensive only — the route still
// renders; this just makes sure nav doesn't suggest surfaces the user can't act
// on."
//
// 🚨 WHAT THAT PRODUCED, REPORTED BY THE PEOPLE LIVING IN IT. Jesse: "Nick can
// view the Dashboard page (which is for admins only), but sees an error box at
// the top saying not everything is loading." Nick, in a ticket: "can someone fix
// my permissions, im still getting Couldn't load ownership (Insufficient
// permissions for borrower data). on pretty much everything in my account." And
// a second Partner Success rep: everything he tries gives him an error.
//
// 🔑 AND THEY WERE RIGHT, AND NOTHING WAS BROKEN. The server guard
// (backend/constants/staffRoles.js) is default-closed and was refusing exactly
// what it should. Every one of those errors was a page the product had offered
// them and the server then correctly declined — a dead affordance, not a bug in
// the guard. Jesse: "If they do not/should not have access, they should not be
// able to view or see the page at all."
//
// ⚠️ SO THE RULE LIVES HERE ONCE AND THREE SURFACES DERIVE FROM IT:
//
//   the sidebar   filters its items, as it already did
//   the top tabs  derive visibility from the TARGET of each tab, so a tab
//                 cannot offer a page its own section hides
//   the router    refuses to render a page the role may not see, and sends
//                 them somewhere they can work instead of showing them a
//                 broken one
//
// 🔑 AND THE LANDING PAGE IS DERIVED, NOT LISTED. A per-role "home" list would
// be a fourth thing to keep true; landingPathFor() returns the first item the
// role's own nav offers, so it cannot point at a page they cannot open. The
// login redirect sent every role to /admin/dashboard, which is how a Partner
// Success rep came to start every session on a page their own nav hides.
// ─────────────────────────────────────────────────────────────────────────────

import {
  NO_BORROWER_DATA, NO_BORROWER_SECTION, SCOPED_BORROWER_READ_ROLES,
} from './staffRoleAccess';

// Per-item `hideForRoles: [...]` removes that item from the sidebar for
// listed roles. Items without the field are visible to every role.
// Role keys match the canonical values stored in users.role
// (contract_desk = "Real Estate Desk" — see middleware/adminAuth.js).
//
// Items with badgeKey 'messages' or 'alerts' get live count badges.
export const SIDEBAR_CONFIGS = [
  {
    match: (p) => p === '/admin' || p.startsWith('/admin/dashboard') || p === '/admin/messages' || p === '/admin/alerts',
    // Whole-config role gate — RE desk never lands in the dashboard
    // sidebar surface because they don't have dashboard access (they
    // navigate via /admin/borrowers instead). Defensive only — the
    // route still renders; this just makes sure nav doesn't suggest
    // surfaces the user can't act on.
    hideForRoles: ['contract_desk'],
    items: [
      // ── 🚨 SIX OF THESE EIGHT ARE BORROWER AND COMPANY DATA ─────────────
      //
      // borrowerDataGuard refuses them for partner_success, correctly. The nav
      // offered them anyway, so a Partner Success rep saw eight tabs and six
      // loaded nothing at all — the dead-affordance shape, and the same one
      // that made Check Eligibility and Report a Bug look broken rather than
      // forbidden.
      //
      // 🔑 THE GROUP IS NOT HIDDEN WHOLESALE, because two tabs are this role's
      // own work: Partner Activity (counts and a partner leaderboard from the
      // same partnerKpis module the Partners tab uses) and Tickets, which is
      // where PARTNER tickets are worked and the reason /tickets is allow-listed
      // at all. Messages stays too — the threads list forces sender_type
      // 'partner' server-side for this role, so it works.
      // 🔑 NO_BORROWER_DATA, NOT ['partner_success']. These six read borrower and
      // company figures, so the roles the SERVER refuses that data to are
      // exactly the roles that must not be offered the tab — derived rather
      // than listed, because engineering was added to the role enum later, is
      // refused the same data for the same reason, and nobody came back here.
      { label: 'Overview', path: '/admin/dashboard', hideForRoles: NO_BORROWER_DATA },
      { label: 'Marketing', path: '/admin/dashboard/marketing', hideForRoles: NO_BORROWER_DATA },
      { label: 'Sales', path: '/admin/dashboard/sales', hideForRoles: NO_BORROWER_DATA },
      { label: 'Operations', path: '/admin/dashboard/operations', hideForRoles: NO_BORROWER_DATA },
      { label: 'Partner Activity', path: '/admin/dashboard/partner-activity' },
      { label: 'Funding', path: '/admin/dashboard/funding', hideForRoles: NO_BORROWER_DATA },
      { label: 'Loan Performance', path: '/admin/dashboard/loan-performance', hideForRoles: NO_BORROWER_DATA },
      { label: 'Tickets', path: '/admin/dashboard/customer-service', badgeKey: 'tickets' },
      { type: 'divider' },
      { label: 'Messages', path: '/admin/messages', badgeKey: 'messages' },
      // ⚠️ THE MIXED FEED. GET /alerts takes ?category=borrower|partner|all and
      // this page asks for 'all', so it is refused for partner_success — and
      // must stay refused until the endpoint forces the category server-side the
      // way the threads list does. Partners › Alerts is the one that works for
      // this role, and it is two clicks away.
      { label: 'Alerts', path: '/admin/alerts', badgeKey: 'alerts', hideForRoles: NO_BORROWER_DATA },
    ],
  },
  {
    // /admin/underwriting-guide lives in the borrower-section sidebar so
    // the same nav stays present when admins jump from a borrower's
    // Credit Review tab over to the guide for reference.
    match: (p) => p.startsWith('/admin/borrowers') || p === '/admin/underwriting-guide' || p.startsWith('/admin/modules') || p.startsWith('/admin/reports'),
    // ── 🚨 THE SECTION GATE IS NOT THE PAGE GATE ANY MORE ───────────────────
    //
    // It was NO_BORROWER_DATA — derived from the server's BORROWER_DATA_ROLES,
    // which is still the right rule for a page that reads the full borrower
    // file. But partner_success now has READ-ONLY, ROW-SCOPED access to two
    // pages in here (Leads and Borrower Pipeline, their own partners'
    // referrals only — see SCOPED_BORROWER_READ_ROLES), and hiding the whole
    // section would hide the two pages the role is allowed.
    //
    // 🔑 SO TWO LISTS, EACH ANSWERING ITS OWN QUESTION. NO_BORROWER_SECTION is
    // "may not see this section at all"; NO_BORROWER_DATA stays on every item
    // that reads the full file. The two allowed items carry neither.
    //
    // ⚠️ AND THE SUBTREE RULE IS WHAT NO LONGER COVERS EVERYTHING, so the gap it
    // used to close has to be closed deliberately. This match includes
    // /admin/borrowers/:id — a borrower PROFILE, named by no nav item — which
    // partner_success must NOT reach. The server refuses it (no profile route is
    // in SCOPED_BORROWER_READ_ROUTES), and maySeePath below refuses it too via
    // SCOPED_SECTION_PATHS: for a scoped role, only the two listed paths in this
    // section are visible, not the whole match.
    hideForRoles: NO_BORROWER_SECTION,
    // ── 🚨 WHAT A SCOPED ROLE MAY SEE IN THIS SECTION, ENUMERATED ───────────
    //
    // This config's `match` covers the whole /admin/borrowers subtree, which
    // used to be exactly what made the guard complete: a borrower PROFILE is
    // named by no nav item, and the section rule caught it.
    //
    // 🚨 OPENING THE SECTION TO partner_success REMOVED THAT. maySeePath returns
    // true when no ITEM covers a path, and nothing covers /admin/borrowers/123
    // — Borrower Pipeline is `exact`. So a scoped rep could navigate to a
    // borrower profile, the server would refuse it (correctly — no profile
    // route is in SCOPED_BORROWER_READ_ROUTES), and they would get the dead
    // affordance this whole module exists to remove.
    //
    // 🔑 SO FOR A SCOPED ROLE THIS LIST IS THE WHOLE SECTION. Anything not named
    // here is refused — default-closed, the same discipline as the server's
    // route list, and for the same reason: a page added under this match
    // tomorrow is denied until somebody comes here and decides otherwise.
    scopedVisiblePaths: ['/admin/borrowers/leads', '/admin/borrowers'],
    items: [
      // Leads sits ABOVE Borrower Pipeline deliberately: it is the top of the
      // funnel, and everything below it is someone who has already applied.
      //
      // 🔑 THESE TWO CARRY NO hideForRoles — they are the grant. Everything
      // below keeps NO_BORROWER_DATA or its existing rule, so partner_success
      // sees exactly two entries in this section.
      { label: 'Leads', path: '/admin/borrowers/leads' },
      { label: 'Borrower Pipeline', path: '/admin/borrowers', exact: true },
      // Active Modules — collapsible parent right under Borrower Pipeline.
      // Property Modules = RE Desk acquisition queue (admin + contract_desk);
      // Underwriting Modules = loan-side (admin/LO), hidden from RE Desk.
      { label: 'Active Modules', hideForRoles: NO_BORROWER_DATA, children: [
        { label: 'Property Modules', path: '/admin/modules/property' },
        { label: 'Underwriting Modules', path: '/admin/modules/underwriting', hideForRoles: ['contract_desk'] },
        { label: 'Closing Modules', path: '/admin/modules/closing', hideForRoles: ['contract_desk'] },
      ] },
      // Loan Tape / Historicals / Underwriting Guide are LO + underwriter
      // tools — surface them to admin only, hide for RE Desk who is
      // working acquisitions rather than the loan book.
      { label: 'Loan Tape', path: '/admin/borrowers/loan-tape', hideForRoles: ['contract_desk', ...NO_BORROWER_DATA] },
      { label: 'Historicals', path: '/admin/borrowers/historicals', hideForRoles: ['contract_desk', ...NO_BORROWER_DATA] },
      { label: 'Underwriting Guide', path: '/admin/underwriting-guide', hideForRoles: ['contract_desk', ...NO_BORROWER_DATA] },
      // Reports moved out of the top nav into here. Same hideForRoles the
      // standalone Reports section used (admin + account_manager only).
      { label: 'Reports', hideForRoles: ['contract_desk', 'loan_officer', ...NO_BORROWER_DATA], children: [
        { label: 'Pipeline Reports', path: '/admin/reports/pipeline' },
        { label: 'Revenue Reports', path: '/admin/reports/revenue' },
        { label: 'Call Reports', path: '/admin/reports/calls' },
      ] },
      { type: 'divider' },
      { label: 'Messages', path: '/admin/borrowers/messages', badgeKey: 'messages', hideForRoles: NO_BORROWER_DATA },
      { label: 'Alerts', path: '/admin/borrowers/alerts', badgeKey: 'borrowerAlerts', hideForRoles: NO_BORROWER_DATA },
      // Showing/Offer Requests live with the Real Estate Desk workflow —
      // Loan Officers don't need them in nav (LOs work the loan side,
      // not acquisitions). Hidden for loan_officer; visible to admin
      // and contract_desk.
      { label: 'Showing Requests', path: '/admin/borrowers/showing-requests', badgeKey: 'showingRequests', hideForRoles: ['loan_officer', ...NO_BORROWER_DATA] },
      { label: 'Offer Requests', path: '/admin/borrowers/offer-requests', badgeKey: 'offerRequests', hideForRoles: ['loan_officer', ...NO_BORROWER_DATA] },
    ],
  },
  {
    match: (p) => p.startsWith('/admin/partners') || p.startsWith('/admin/leads'),
    // Partners portal is operated by the partner-success team, not RE Desk.
    hideForRoles: ['contract_desk'],
    items: [
      { label: 'Leads', path: '/admin/partners/leads' },
      { label: 'Partners', path: '/admin/partners/partners' },
      { label: 'Accounts', path: '/admin/partners/accounts' },
      { label: 'Referrals', path: '/admin/partners/referrals' },
      // 🔑 THE ONLY ENTRY HERE THAT SAYS WHAT NEEDS DOING rather than what
      // exists. Every row on it is derived — see pages/AdminPartnerTasks.jsx.
      // ⚠️ THE BADGE IS A COUNT OF OUTSTANDING ROWS, NOT OF UNREAD ONES. Nothing
      // here is read or unread — every row is derived from a partner's record and
      // disappears when the thing it describes is dealt with, so there is no read
      // state to keep and nothing to mark.
      { label: 'Tasks', path: '/admin/partners/tasks', badgeKey: 'partnerTasks' },
      // Every open licence-check task (utils/licenceCheckAlerts), all partners — the digest on screen (L4 A).
      { label: 'Licence checks', path: '/admin/partners/licence-checks' },
      // The partner portal's buyer-eligibility check, same component and same
      // backend handler — staff answering "would this buyer qualify?" see exactly
      // what the partner sees. See pages/AdminPartnerEligibility.jsx.
      { label: 'Check Eligibility', path: '/admin/partners/eligibility' },
      { label: 'Meetings', path: '/admin/partners/meetings' },
      { type: 'divider' },
      { label: 'Messages', path: '/admin/partners/messages', badgeKey: 'partnerMessages' },
      { label: 'Alerts', path: '/admin/partners/alerts', badgeKey: 'partnerAlerts' },
    ],
  },
  {
    // Marketing hub — content / ads / PR / SEO agents + Surveys.
    // Admin-only by explicit owner decision (2026-06-01) — RE Desk + LO
    // don't see this group at all. Group-level hideForRoles is the
    // single source of truth; per-item hideForRoles dropped since the
    // entire group is now gated.
    match: (p) => p.startsWith('/admin/marketing') || p.startsWith('/admin/surveys'),
    hideForRoles: ['contract_desk', 'loan_officer', 'partner_success'],
    items: [
      // Overview removed — was a 5-card launcher grid duplicating
      // exactly these sidebar items. /admin/marketing now redirects
      // to /admin/marketing/content (Content Queue) via the App.js
      // route, so direct hits still land somewhere usable.
      { label: 'Content Queue', path: '/admin/marketing/content' },
      { label: 'Ad Campaigns',  path: '/admin/marketing/ads' },
      { label: 'PR Outreach',   path: '/admin/marketing/pr' },
      { label: 'SEO Agent',     path: '/admin/marketing/seo' },
      { type: 'divider' },
      { label: 'Surveys', path: '/admin/surveys' },
    ],
  },
  {
    // Finance hub — reconciliation / P&L / wires / spending.
    // Admin-only by explicit owner decision (2026-06-01) — RE Desk + LO
    // don't see this group at all. Group-level hideForRoles is the
    // single source of truth.
    match: (p) => p.startsWith('/admin/finance'),
    hideForRoles: ['contract_desk', 'loan_officer', 'partner_success'],
    items: [
      { label: 'Overview',           path: '/admin/finance', exact: true },
      { label: 'Wire Requests',      path: '/admin/finance/wire-requests' },
      { label: 'P&L',                path: '/admin/finance/pl' },
      { label: 'Reconciliation',     path: '/admin/finance/reconciliation' },
      { label: 'Spending Analysis',  path: '/admin/finance/spending' },
      { label: 'Recipients',         path: '/admin/finance/recipients' },
      { label: 'Partner Fees',       path: '/admin/finance/partner-fees' },
      { label: 'Acquisition',        path: '/admin/finance/acquisition' },
      { label: 'Warehouse',          path: '/admin/finance/warehouse' },
    ],
  },
  {
    match: (p) => p.startsWith('/admin/schedule'),
    // ⚠️ THE SCHEDULE IS KEY LOAN DATES AND PROPERTY ACQUISITION — borrower
    // data by another name, so the same derived rule applies. Partner Meetings
    // is the one view that is not, and it is reachable from the Partners
    // section (Meetings) which this role does have.
    hideForRoles: NO_BORROWER_DATA,
    items: [
      { label: 'All Events', path: '/admin/schedule?view=all' },
      { type: 'divider' },
      { label: 'Key Loan Dates', path: '/admin/schedule?view=loan_dates' },
      { label: 'Property Acquisition', path: '/admin/schedule?view=acquisition' },
      { label: 'Partner Meetings', path: '/admin/schedule?view=meetings' },
    ],
  },
  {
    match: (p) => p.startsWith('/admin/market'),
    // DoorlyMarket is capital-markets / investor-facing — outside RE Desk scope.
    hideForRoles: ['contract_desk', 'partner_success'],
    items: [
      { label: 'Overview', path: '/admin/market/dashboard' },
      { label: 'Pools', path: '/admin/market/pools' },
      { label: 'Investors', path: '/admin/market/investors' },
      { label: 'Bids', path: '/admin/market/bids' },
      { type: 'divider' },
      { label: 'Messages', path: '/admin/market/messages', badgeKey: 'marketMessages' },
      { label: 'Alerts', path: '/admin/market/alerts', badgeKey: 'marketAlerts' },
    ],
  },
  {
    // Settings section — visible only when the role-gated Settings nav
    // link is active (the AdminNavbar tab itself is gated on
    // adminUser.role === 'admin', so non-admins never reach a path
    // matching /admin/settings).
    // ⚠️ /admin/ota IS IN THE MATCH DELIBERATELY. It lives outside
    // /admin/settings, so without it the sidebar would vanish the moment you
    // followed the link — and the page would become a dead end of its own.
    match: (p) => p.startsWith('/admin/settings') || p === '/admin/ota',
    items: [
      { label: 'Users', path: '/admin/settings/users' },
      // ⚠️ NOTHING LINKED HERE UNTIL NOW, WHICH IS WHY IT WENT UNUSED. The page
      // has existed since the OTA work and carries both the device channel
      // switcher (put THIS device on staging to smoke-test a bundle before it is
      // promoted) and the header-debug toggle. Both were unreachable in the
      // native app — no address bar — so a debug overlay had to be shipped to
      // borrowers to take a measurement the toggle already supported.
      { label: 'App Updates (OTA)', path: '/admin/ota' },
    ],
  },
];


// ─────────────────────────────────────────────────────────────────────────────
// THE TOP-LEVEL TABS.
//
// ⚠️ THESE CARRY NO ROLE RULES OF THEIR OWN, DELIBERATELY. They used to carry
// none at all, which is a different thing: every role saw Dashboard, Borrowers,
// Marketing, Finance and Schedule whether or not the section behind them was
// theirs. Visibility is now DERIVED from the target path through the same
// maySeePath() the router uses, so a tab cannot offer a page its own section
// hides — and adding a role rule to a section automatically removes its tab.
// ─────────────────────────────────────────────────────────────────────────────
export const NAV_LINKS = [
  { to: '/admin/dashboard', label: 'Dashboard' },
  { to: '/admin/dashboard/customer-service', label: 'Tickets' },
  { to: '/admin/borrowers', label: 'Borrowers' },
  { to: '/admin/partners', label: 'Partners' },
  { to: '/admin/marketing', label: 'Marketing' },
  { to: '/admin/finance', label: 'Finance' },
  { to: '/admin/schedule', label: 'Schedule' },
];

/**
 * 🔑 PATHS THE GUARD MUST NEVER REFUSE, because they are how somebody gets a
 * role in the first place. These run before there is a session to read a role
 * from, so "is this role allowed here" is not a question that applies.
 */
export const UNGUARDED_ADMIN_PATHS = [
  '/admin',
  '/admin/login',
  '/admin/accept-invite',
  '/admin/reset-password',
  // ⚠️ THE EXPLAINER ITSELF. A role sent here by the guard must be able to read
  // why, and a guard that could refuse its own explanation would loop.
  '/admin/no-access',
  // 🔑 THE MANUAL IS FOR EVERY ROLE and is not in any nav section.
  '/admin/manual',
];

/**
 * ⚠️ desk.godoorly.com SERVES THE SAME ROUTES WITHOUT THE /admin PREFIX, so
 * every rule in this file would miss there. The route table is mounted at both
 * '' and '/admin' (see App.js adminRoutes), and the nav's own paths are all
 * /admin-prefixed, so normalising to the prefixed form is what makes one set of
 * rules cover both hostnames.
 */
export function normaliseAdminPath(pathname) {
  const p = String(pathname || '/');
  if (p === '/admin' || p.startsWith('/admin/')) return p;
  return p === '/' ? '/admin' : `/admin${p}`;
}

const hidden = (node) => (node && node.hideForRoles) || [];

/** Every leaf item of a config, flattened through `children` groups. */
function leaves(config) {
  const out = [];
  for (const item of config.items || []) {
    if (item.type === 'divider') continue;
    if (item.children) {
      for (const child of item.children) {
        if (child.type === 'divider') continue;
        // 🔑 A CHILD INHERITS ITS GROUP'S RULE. "Reports" hides two roles and
        // its children name none; reading the child alone would grant them.
        out.push({ ...child, hideForRoles: [...hidden(item), ...hidden(child)] });
      }
      continue;
    }
    out.push(item);
  }
  return out;
}

/**
 * 🚨 MAY THIS ROLE SEE THIS PAGE?
 *
 * ⚠️ UNKNOWN PATHS ARE ALLOWED, AND THAT IS A STATED LIMIT RATHER THAN AN
 * OVERSIGHT. The nav is not a complete route table — borrower profiles, ticket
 * details and settings pages are reachable and listed nowhere — so a
 * default-deny here would lock every role out of pages their own section links
 * to. The SERVER is the gate and is default-closed; this decides what the
 * product OFFERS, and refusing to render a page nobody has classified would
 * replace a dead affordance with a dead end.
 *
 * 🔑 A SECTION RULE COVERS ITS WHOLE SUBTREE, which is what makes the common
 * case complete: the Borrowers config matches /admin/borrowers*, so a borrower
 * profile is refused for partner_success even though no nav item names it.
 */
export function maySeePath(role, pathname) {
  const path = normaliseAdminPath(pathname);
  if (UNGUARDED_ADMIN_PATHS.includes(path)) return true;

  const config = SIDEBAR_CONFIGS.find((c) => c.match(path));
  if (!config) return true;                       // see the note above
  if (hidden(config).includes(role)) return false; // the whole section

  // ── 🚨 A SCOPED ROLE SEES ONLY WHAT THE SECTION ENUMERATES ───────────────
  //
  // Checked BEFORE the item sweep below, because that sweep returns true when
  // nothing covers the path — which is every borrower profile under
  // /admin/borrowers/:id. See scopedVisiblePaths on the Borrowers config.
  if (SCOPED_BORROWER_READ_ROLES.includes(role) && Array.isArray(config.scopedVisiblePaths)) {
    if (config.scopedVisiblePaths.some((p) => path === String(p).split('?')[0])) return true;
    // ── 🔑 AND THE BORROWER FILE A ROW OPENS ────────────────────────────────
    //
    // Jesse: "when you click a row/borrower on the Borrower Pipeline or Leads
    // table, they should have access to view all tabs as read only (no edit
    // access) - right now it sends to a page that says 'Not a part of your
    // role'." Every row on both granted pages was a link to a page this rule
    // refused, which is a dead affordance on the only two pages the role has.
    //
    // ⚠️ DIGITS ONLY, AND THAT IS NOT COSMETIC. /admin/borrowers/leads is a LIST
    // at the same depth; a loose segment match would fold it into the profile
    // rule, and /admin/borrowers/adversed and /archived with it.
    //
    // ⚠️ THE SERVER IS WHAT MAKES THIS SAFE, not this line. The nav decides what
    // is OFFERED; middleware/borrowerDataGuard decides what is SERVED, and it
    // refuses any borrower outside the rep's own partners before a handler runs.
    // Showing a page the server would refuse is the failure this rule exists to
    // prevent, so the two are deliberately the same shape.
    // 🔑 AND bs-<n>, which is a broker SUBMISSION reference: the profile page forwards it to the submission view
    // (Jesse, 2026-11-02). Still never a list name — the prefix is fixed and the rest is digits.
    return /^\/admin\/borrowers\/(\d+|bs-\d+)(\/.*)?$/.test(path);
  }

  // The most specific item that covers this path, if any.
  const items = leaves(config).filter((i) => i.path);
  const bare = (p) => String(p).split('?')[0];
  const covering = items
    .filter((i) => path === bare(i.path) || (!i.exact && path.startsWith(`${bare(i.path)}/`)))
    .sort((a, b) => bare(b.path).length - bare(a.path).length)[0];
  if (covering && hidden(covering).includes(role)) return false;
  return true;
}

/**
 * 🔑 WHERE THIS ROLE SHOULD START, DERIVED FROM WHAT THEIR NAV OFFERS.
 *
 * The first leaf of the first section they can see. A hand-written per-role home
 * would be a fourth list to keep true, and the failure it invites is precisely
 * the one being fixed: /admin/dashboard was everybody's landing page and is
 * hidden from Partner Success.
 *
 * ⚠️ THE FALLBACK IS THE EXPLAINER, NOT THE DASHBOARD. A role with nothing
 * visible at all must not be bounced to a page it cannot open — that is the
 * redirect loop this guard would otherwise create on its own landing.
 */
export function landingPathFor(role) {
  for (const config of SIDEBAR_CONFIGS) {
    if (hidden(config).includes(role)) continue;
    for (const item of leaves(config)) {
      if (!item.path) continue;
      if (hidden(item).includes(role)) continue;
      if (!maySeePath(role, item.path)) continue;
      return item.path;
    }
  }
  return '/admin/no-access';
}

/** The top tabs this role may actually use. */
export function visibleNavLinks(role) {
  return NAV_LINKS.filter((l) => maySeePath(role, l.to));
}

/**
 * 🚨 WHICH BADGE COUNTS THE SHELL MAY POLL FOR THIS ROLE.
 *
 * The sidebar and the mobile sub-nav polled every badge feed for every role,
 * then hid the badges a role could not see. For Partner Success that was four
 * refused calls on every page — borrower alerts, borrower request counts and
 * market alerts — and one of them, uncaught, zeroed every mobile badge.
 * Jesse (2026-11-01): a widget for an area the role cannot open is not
 * rendered, rather than called and failed.
 *
 * 🔑 DERIVED FROM THE BADGES THE ROLE'S OWN NAV RENDERS, like the top tabs: a
 * feed is polled only when some item carrying its badgeKey is visible to the
 * role (its config, its group and maySeePath all agree). Hiding a section
 * stops its feed with no edit here.
 *
 * ⚠️ THE BELL (GET /alerts/unread-count) IS NOT IN THIS LIST. It is polled for
 * every role and narrowed server-side (utils/partnerScope.staffAlertScope).
 */
export function visibleBadgeKeys(role) {
  const keys = new Set();
  for (const config of SIDEBAR_CONFIGS) {
    if (hidden(config).includes(role)) continue;
    for (const item of leaves(config)) {
      if (!item.badgeKey || !item.path) continue;
      if (hidden(item).includes(role)) continue;
      if (!maySeePath(role, item.path)) continue;
      keys.add(item.badgeKey);
    }
  }
  return keys;
}

/** The feeds behind those badges, by the endpoint the shell calls. */
export function shellBadgeFeeds(role) {
  const k = visibleBadgeKeys(role);
  return {
    borrowerAlerts: k.has('borrowerAlerts'),                         // GET /alerts?category=borrower
    partnerAlerts:  k.has('partnerAlerts'),                          // GET /alerts?category=partner
    requestCounts:  k.has('showingRequests') || k.has('offerRequests'), // GET /borrowers/request-counts
    marketAlerts:   k.has('marketAlerts'),                           // GET /market/alerts/unread-count
    marketMessages: k.has('marketMessages'),                         // GET /api/market/admin/messages/unread-count
  };
}
